// Entries, dictations and X-ray images recorded while offline wait here
// (IndexedDB) until the server takes them. Each carries its clientId, so a
// retry never creates a duplicate. Only that data is stored, and it is deleted
// as soon as it is synced.
import type { NewEntry } from '@/types/api';

export type OutboxItem = {
  clientId: string;
  patientId: string;
  /** The doctor picked in the selector. Items queued by older versions have none. */
  dentistId: string | null;
  /** Added to this visit explicitly (one already confirmed); otherwise the open visit of the day. */
  visitId?: string | null;
  entry: NewEntry;
  authorId: string;
  authorName: string;
  queuedAt: string;
  /** Set when the server refused it (not a network error). */
  failed?: string;
};

/** The latest text of one dictation; a newer copy replaces the older one. */
export type TranscriptItem = {
  clientId: string;
  patientId: string;
  /** The doctor picked in the selector. Items queued by older versions have none. */
  dentistId: string | null;
  lang: string;
  text: string;
  /** When the dictation started. */
  recordedAt: string;
  authorId: string;
  authorName: string;
  queuedAt: string;
  failed?: string;
};

/** One X-ray image, already compressed on the device (JPEG). */
export type XrayItem = {
  clientId: string;
  patientId: string;
  /** The doctor picked in the selector, for a visit the upload opens. */
  dentistId: string | null;
  /** Added to this visit explicitly; otherwise the open visit of the day. */
  visitId: string | null;
  teeth: number[];
  blob: Blob;
  width: number;
  height: number;
  takenAt: string;
  authorId: string;
  authorName: string;
  queuedAt: string;
  failed?: string;
};

const DB_NAME = 'dentlog';
const DB_VERSION = 3;
const ENTRIES = 'outbox';
const TRANSCRIPTS = 'transcripts';
const XRAYS = 'xrays';
type StoreName = typeof ENTRIES | typeof TRANSCRIPTS | typeof XRAYS;
const listeners = new Set<() => void>();

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      for (const name of [ENTRIES, TRANSCRIPTS, XRAYS]) {
        if (req.result.objectStoreNames.contains(name)) continue;
        const store = req.result.createObjectStore(name, { keyPath: 'clientId' });
        store.createIndex('patientId', 'patientId');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(
  name: StoreName,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T> | undefined
): Promise<T | undefined> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const t = db.transaction(name, mode);
    const req = run(t.objectStore(name));
    t.oncomplete = () => {
      db.close();
      resolve(req?.result);
    };
    t.onerror = () => reject(t.error);
  });
}

async function putAll(name: StoreName, items: readonly object[]) {
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(name, 'readwrite');
    for (const item of items) t.objectStore(name).put(item);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
  db.close();
  changed();
}

async function removeAll(name: StoreName, clientIds: readonly string[]) {
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const t = db.transaction(name, 'readwrite');
    for (const id of clientIds) t.objectStore(name).delete(id);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
  db.close();
  changed();
}

async function all<T>(name: StoreName): Promise<T[]> {
  return ((await tx(name, 'readonly', (s) => s.getAll())) as T[] | undefined) ?? [];
}

async function forPatient<T>(name: StoreName, patientId: string): Promise<T[]> {
  return (
    ((await tx(name, 'readonly', (s) => s.index('patientId').getAll(patientId))) as
      | T[]
      | undefined) ?? []
  );
}

function changed() {
  for (const l of listeners) l();
}

export const outbox = {
  add: (items: OutboxItem[]) => putAll(ENTRIES, items),
  all: () => all<OutboxItem>(ENTRIES),
  forPatient: (patientId: string) => forPatient<OutboxItem>(ENTRIES, patientId),
  remove: (clientIds: string[]) => removeAll(ENTRIES, clientIds),
  async markFailed(clientIds: string[], reason: string) {
    const items = (await outbox.all()).filter((i) => clientIds.includes(i.clientId));
    await outbox.add(items.map((i) => ({ ...i, failed: reason })));
  },
  /** Explicit logout: drop this user's unsynced entries and dictations from the device. */
  async removeByAuthor(authorId: string) {
    const ids = (await outbox.all()).filter((i) => i.authorId === authorId).map((i) => i.clientId);
    if (ids.length) await outbox.remove(ids);
    const dictations = (await transcriptOutbox.all())
      .filter((i) => i.authorId === authorId)
      .map((i) => i.clientId);
    if (dictations.length) await removeAll(TRANSCRIPTS, dictations);
    const images = (await xrayOutbox.all())
      .filter((i) => i.authorId === authorId)
      .map((i) => i.clientId);
    if (images.length) await xrayOutbox.remove(images);
  },
  async clear() {
    await tx(ENTRIES, 'readwrite', (s) => s.clear());
    await tx(TRANSCRIPTS, 'readwrite', (s) => s.clear());
    await tx(XRAYS, 'readwrite', (s) => s.clear());
    changed();
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

/** The send of each dictation in progress; a new one waits for the previous one. */
const sending = new Map<string, Promise<unknown>>();

/** Runs `run` after any other send of the same dictation, so two never overlap. */
function oneAtATime<T>(clientId: string, run: () => Promise<T>): Promise<T> {
  const previous = sending.get(clientId) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(run);
  sending.set(clientId, next);
  const done = () => {
    if (sending.get(clientId) === next) sending.delete(clientId);
  };
  next.then(done, done);
  return next;
}

async function findTranscript(clientId: string): Promise<TranscriptItem | undefined> {
  return (await transcriptOutbox.all()).find((i) => i.clientId === clientId);
}

export const transcriptOutbox = {
  put: (item: TranscriptItem) => putAll(TRANSCRIPTS, [item]),
  all: () => all<TranscriptItem>(TRANSCRIPTS),
  forPatient: (patientId: string) => forPatient<TranscriptItem>(TRANSCRIPTS, patientId),
  /**
   * Sends the latest copy of the dictation stored on the device, after any
   * other send of it (the chart and the background sync may both try), so an
   * older text never reaches the server after a newer one. The copy is
   * dropped once sent, unless a newer one replaced it meanwhile. Resolves to
   * `send`'s result, or null when nothing (unfailed) was queued.
   */
  sendLatest<T>(clientId: string, send: (item: TranscriptItem) => Promise<T>): Promise<T | null> {
    return oneAtATime(clientId, async () => {
      const item = await findTranscript(clientId);
      if (!item || item.failed) return null;
      const result = await send(item);
      const current = await findTranscript(clientId);
      if (current && current.text === item.text && current.lang === item.lang) {
        await removeAll(TRANSCRIPTS, [clientId]);
      }
      return result;
    });
  },
  /** Marks the stored copy (the newest one) as refused by the server. */
  async markFailed(clientId: string, reason: string) {
    const current = await findTranscript(clientId);
    if (current) await putAll(TRANSCRIPTS, [{ ...current, failed: reason }]);
  },
};

export const xrayOutbox = {
  add: (item: XrayItem) => putAll(XRAYS, [item]),
  all: () => all<XrayItem>(XRAYS),
  forPatient: (patientId: string) => forPatient<XrayItem>(XRAYS, patientId),
  remove: (clientIds: string[]) => removeAll(XRAYS, clientIds),
  async markFailed(clientId: string, reason: string) {
    const item = (await xrayOutbox.all()).find((i) => i.clientId === clientId);
    if (item) await putAll(XRAYS, [{ ...item, failed: reason }]);
  },
  subscribe: (listener: () => void) => outbox.subscribe(listener),
};
